import React, { useState } from "react";
import { Row, Col } from "antd";
import { SubscriptionGate } from "../components/SubscriptionGate";
import { useOrganization } from "../contexts/organization-context";
import { useSubscription } from "../contexts/subscription-context";
import { TrialBanner } from "../components/TrialBanner";
import { MedicalDisclaimer } from "../components/MedicalDisclaimer";
import { useCodeProcessing } from "../hooks/useCodeProcessing";
import FileUploadSection from "../components/code-identifier/FileUploadSection";
import PaymentStatusAlerts from "../components/code-identifier/PaymentStatusAlerts";
import CodeIdentifierSteps from "../components/code-identifier/CodeIdentifierSteps";
import ResultsContainer from "../components/code-identifier/ResultsContainer";

const CodeIdentifierUserContent: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const { isOrganizationOwner } = useOrganization();
  const { getCodeIdentifierAccess } = useSubscription();
  
  // Use the custom hooks for processing
  const {
    finalEvaluation,
    pdpmRankingAnalysis,
    patientNames,
    s3InputFiles,
    loadingUpload,
    loadingFinalEvaluation,
    processingStatus,
    processingSteps,
    handleUpload,
    hasReceivedResult
  } = useCodeProcessing();

  // Get access information
  const access = getCodeIdentifierAccess();

  // Handle step change
  const handleStepChange = (step: number) => {
    setCurrentStep(step);
  };

  // Handle previous step
  const handlePreviousStep = () => {
    setCurrentStep(0);
  };

  // When processing completes successfully, move to results step
  React.useEffect(() => {
    if (hasReceivedResult && finalEvaluation.length > 0) {
      setCurrentStep(1);
    }
  }, [hasReceivedResult, finalEvaluation.length]);

  return (
    <div>
      {/* Trial Banner - shows when user has access but no subscription, or no access */}
      <div style={{ marginBottom: 24 }}>
        <TrialBanner />
      </div>

      {/* Step-by-Step Progress Indicator */}
      <CodeIdentifierSteps 
        currentStep={currentStep}
        onStepChange={handleStepChange}
      />

      {/* Payment/Subscription Status Alerts */}
      <PaymentStatusAlerts 
        access={access}
        isOrganizationOwner={isOrganizationOwner}
      />

      {/* File Upload Section */}
      {currentStep === 0 && (
        <FileUploadSection
          onUpload={handleUpload}
          loadingUpload={loadingUpload}
          processingStatus={processingStatus}
          processingSteps={processingSteps}
          accessible={access.accessible}
        />
      )}

      {/* Results Section with Tabs */}
      {currentStep === 1 && (
        <ResultsContainer
          finalEvaluation={finalEvaluation}
          pdpmRankingAnalysis={pdpmRankingAnalysis}
          patientNames={patientNames}
          s3InputFiles={s3InputFiles}
          loadingFinalEvaluation={loadingFinalEvaluation}
          onPreviousStep={handlePreviousStep}
        />
      )}

      {/* Medical Disclaimer */}
      <Row justify="center" style={{ marginTop: 24 }}>
        <Col xs={24} sm={24} md={18} lg={16} xl={14}>
          <MedicalDisclaimer />
        </Col>
      </Row>
    </div>
  );
};

const CodeIdentifierUser: React.FC = () => {
  return (
    <SubscriptionGate feature="the Code Identification tool">
      <CodeIdentifierUserContent />
    </SubscriptionGate>
  );
};

export { CodeIdentifierUser };
